/**
 * Receipt resolution queue.
 *
 * The one screen where a human states which MONTHS a payment earned. That single
 * decision is what the settled-month bug used to make silently and wrongly — a
 * client paying June's invoice on July 3rd funded July, so June looked unfunded
 * and blocked its owners while July paid owners on money owed to June.
 *
 * The matcher's opinion is shown, never applied. There is no virtual-account
 * table, so nothing can auto-confirm; the explanation exists to tell you why it
 * needs you, not to be rubber-stamped.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, RefreshCw, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { receiptsAPI, type QueuedReceipt, type PeriodNeed } from '../api';
import { projectAPI } from '@/utils/supabase/client';

function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function SuggestionBadge({ s }: { s: QueuedReceipt['suggestion'] }) {
  const variant =
    s.status === 'confirmed' ? 'default' : s.status === 'flagged' ? 'destructive' : 'secondary';
  return <Badge variant={variant}>{s.reason.replace(/_/g, ' ')}</Badge>;
}

function ResolveCard({ receipt, clients, onDone }: {
  receipt: QueuedReceipt;
  clients: Array<{ id: string; name: string }>;
  onDone: () => void;
}) {
  const [clientId, setClientId] = useState(receipt.suggestion.clientId ?? '');
  const [kind, setKind] = useState<'service' | 'performance' | ''>('');
  const [periods, setPeriods] = useState<PeriodNeed[]>([]);
  const [legs, setLegs] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [loadingPeriods, setLoadingPeriods] = useState(false);

  useEffect(() => {
    if (!clientId) {
      setPeriods([]);
      return;
    }
    setLoadingPeriods(true);
    receiptsAPI
      .periodsFor(receipt.id, clientId)
      .then((r) => setPeriods(r.periods))
      .catch(() => setPeriods([]))
      .finally(() => setLoadingPeriods(false));
  }, [clientId, receipt.id]);

  const legTotalCents = useMemo(
    () =>
      Object.values(legs).reduce((s, v) => {
        const n = Math.round(parseFloat(v || '0') * 100);
        return s + (Number.isFinite(n) ? n : 0);
      }, 0),
    [legs],
  );

  const remainingCents = receipt.amountCents - legTotalCents;
  const overAllocated = legTotalCents > receipt.amountCents;

  const submit = useCallback(async () => {
    if (!clientId || !kind) return;
    const payload = Object.entries(legs)
      .map(([period, v]) => ({ period, amountCents: Math.round(parseFloat(v || '0') * 100) }))
      .filter((l) => l.amountCents > 0);
    if (payload.length === 0) return;

    setSubmitting(true);
    try {
      await receiptsAPI.resolve(receipt.id, clientId, kind, payload);
      toast.success('Applied', {
        description: `${formatCents(legTotalCents)} across ${payload.length} period(s).`,
      });
      onDone();
    } catch (e) {
      toast.error('Could not apply', { description: (e as Error).message });
    } finally {
      setSubmitting(false);
    }
  }, [clientId, kind, legs, legTotalCents, receipt.id, onDone]);

  const matchedPeriods = periods.filter((p) => !kind || p.stream === kind);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="tabular-nums">{formatCents(receipt.amountCents)}</CardTitle>
            <CardDescription>
              {receipt.counterparty ?? 'No counterparty'}
              {receipt.settledAt ? ` · settled ${new Date(receipt.settledAt).toLocaleDateString()}` : ''}
              {receipt.entrySource === 'manual' ? ' · entered by hand' : ''}
            </CardDescription>
          </div>
          <SuggestionBadge s={receipt.suggestion} />
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Why this needs you</AlertTitle>
          <AlertDescription>{receipt.suggestion.explanation}</AlertDescription>
        </Alert>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Client</label>
            <select
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={clientId}
              onChange={(e) => { setClientId(e.target.value); setLegs({}); }}
            >
              <option value="">Select a client…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Stream</label>
            <select
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={kind}
              onChange={(e) => { setKind(e.target.value as 'service' | 'performance' | ''); setLegs({}); }}
            >
              {/* Never defaulted. A mislabelled kicker is counted twice and
                  carries overhead it should not. */}
              <option value="">Select a stream…</option>
              <option value="service">Service (retainer)</option>
              <option value="performance">Performance (kicker)</option>
            </select>
          </div>
        </div>

        {clientId && kind && (
          <>
            <Separator />
            {loadingPeriods ? (
              <p className="text-sm text-muted-foreground">Loading what this client owes…</p>
            ) : matchedPeriods.length === 0 ? (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>No obligations for this client and stream</AlertTitle>
                <AlertDescription>
                  Nothing is recorded as owed, so this cash has nowhere to go. Obligations are
                  materialised from the contract — check the client is enabled for payouts and that
                  an invoice covers the months in question.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-2">
                <p className="text-sm">
                  Which months did this payment earn? Not when it settled — when the work was done.
                </p>
                {matchedPeriods.map((p) => (
                  <div key={`${p.period}-${p.stream}`} className="flex items-center gap-3 text-sm">
                    <span className="w-20 font-mono">{p.period}</span>
                    <span className="flex-1 text-muted-foreground">
                      {formatCents(p.remainingCents)} still unfunded of {formatCents(p.owedCents)}
                    </span>
                    <Input
                      className="w-32 tabular-nums"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={legs[p.period] ?? ''}
                      onChange={(e) => setLegs((l) => ({ ...l, [p.period]: e.target.value }))}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setLegs((l) => ({
                          ...l,
                          [p.period]: (Math.min(p.remainingCents, Math.max(remainingCents + Math.round(parseFloat(l[p.period] || '0') * 100), 0)) / 100).toFixed(2),
                        }))
                      }
                    >
                      Fill
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {legTotalCents > 0 && (
          <div className={`text-sm ${overAllocated ? 'text-destructive' : 'text-muted-foreground'}`}>
            Allocating {formatCents(legTotalCents)} of {formatCents(receipt.amountCents)}
            {overAllocated
              ? ' — more than the payment. Over-applying would invent revenue.'
              : remainingCents > 0
                ? ` · ${formatCents(remainingCents)} will stay unapplied as credit`
                : ' · fully applied'}
          </div>
        )}

        <div className="flex justify-end">
          <Button
            disabled={!clientId || !kind || legTotalCents <= 0 || overAllocated || submitting}
            onClick={submit}
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Check className="mr-2 h-4 w-4" />
            Apply
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function ReceiptQueuePage() {
  const [receipts, setReceipts] = useState<QueuedReceipt[]>([]);
  const [clients, setClients] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Clients come through the API, not supabase-js: `clients` carries no
      // anon/authenticated grant, so a direct table read returns nothing and
      // the picker silently comes up empty. Every other screen uses getClients().
      const [q, cl] = await Promise.all([
        receiptsAPI.queue(),
        projectAPI.getClients(),
      ]);
      setReceipts(q.receipts);
      setClients(
        cl
          .map((c) => ({ id: c.id, name: c.company_name }))
          // The endpoint orders by priority tier; a picker wants alphabetical.
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Unresolved cash</h1>
          <p className="text-sm text-muted-foreground">
            Every payment needs a person to say which client it belongs to and which months it
            earned. Nothing here is applied automatically.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Could not load the queue</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!loading && receipts.length === 0 && !error && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Nothing waiting. Deposits appear here within a few hours of landing in Mercury.
          </CardContent>
        </Card>
      )}

      {receipts.map((r) => (
        <ResolveCard key={r.id} receipt={r} clients={clients} onDone={() => void load()} />
      ))}
    </div>
  );
}
